import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  Stack,
  Grid,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Autocomplete,
  FormGroup,
  Checkbox,
  Divider,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DateCalendar, PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import { ArrowBack, ArrowForward, Delete, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useCentros } from '../../../../hooks/useCentros';
import {
  useDiasEspeciales,
  DiaEspecial,
  CreateEspecialPayload,
} from '../../../../hooks/useDiasEspeciales';
import { useProfesorFormatos } from '../../../../hooks/useProfesorFormatos';
import { WeeklyContextSnapshot } from './ProfesorDisponibilidadPage';

/* ═════════ helpers y bits ═════════ */
const toMinutes = (hms: string) => {
  const [h, m] = hms.split(':').map(Number);
  return h * 60 + m;
};
const toStr = (min: number) =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
const toStrForInput = (min: number) => {
  const capped = Math.max(0, Math.min(min, 23 * 60 + 59));
  return toStr(capped);
};
const parseTimeToMin = (val: string): number | null => {
  if (!val) return null;
  const [h, m] = val.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return Math.max(0, Math.min(h * 60 + m, 23 * 60 + 59));
};
const floorToStep = (min: number, step = 15) =>
  Math.max(0, Math.min(Math.floor(min / step) * step, 23 * 60 + 45));

const BIT_P = 1;
const BIT_O = 2;
const BIT_D = 4;
const MASK_ALL = BIT_P | BIT_O | BIT_D;

/** normaliza "HH:mm[:ss]" → "HH:mm" */
const hhmm = (s?: string | null) => (s ? String(s).slice(0, 5) : (s ?? ''));

/** mask numérica → array de strings requerido por el backend */
const maskToArray = (mask: number): Array<'presencial' | 'online' | 'a_domicilio'> => {
  const out: Array<'presencial' | 'online' | 'a_domicilio'> = [];
  if (mask & BIT_P) out.push('presencial');
  if (mask & BIT_O) out.push('online');
  if (mask & BIT_D) out.push('a_domicilio');
  return out.length ? out : (['presencial'] as any);
};

/* ═════════ día coloreado en calendario ═════════ */
type DayProps = PickersDayProps<Dayjs> & {
  special?: boolean;
  blocked?: boolean; // día bloqueado (día completo)
  chosen?: boolean; // selección múltiple
};
function ColoredDay({ special, blocked, chosen, ...rest }: DayProps) {
  return (
    <PickersDay
      {...rest}
      sx={(theme) => ({
        ...(special && {
          bgcolor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
        }),
        ...(blocked && {
          bgcolor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
        }),
        ...(chosen && {
          bgcolor: theme.palette.grey[200],
          color: theme.palette.text.primary,
          boxShadow: `inset 0 0 0 2px ${theme.palette.grey[300]}`,
          '&:hover': { bgcolor: theme.palette.grey[300] },
        }),
        '&.Mui-selected': {
          bgcolor: chosen ? `${theme.palette.grey[200]} !important` : 'transparent !important',
          color: chosen ? theme.palette.text.primary : 'inherit',
        },
        '&.Mui-selected:hover': {
          bgcolor: chosen ? `${theme.palette.grey[300]} !important` : 'transparent !important',
        },
      })}
    />
  );
}

/* ═════════ diálogo Día especial ═════════ */
interface DlgProps {
  open: boolean;
  date: string | string[];
  profesorId: number;
  existentes: DiaEspecial[];
  defaultRange: [number, number] | null;
  onClose: () => void;
  onSave: (p: { crear: CreateEspecialPayload[]; eliminar: number[] }) => Promise<void>;
}
type Range = [number, number];

type DialogRange = {
  key: string;
  range: Range;
  mask: number;
  centros: number[];
};

const makeDialogRangeKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const dedupeCentros = (values: number[]) =>
  Array.from(new Set(values.filter((v) => Number.isFinite(v)).map(Number)));

const normalizeMask = (mask: number, assignedMask: number) => {
  if (mask > 0) return mask;
  return assignedMask > 0 ? assignedMask : MASK_ALL;
};

const isOnlyOnlineMask = (mask: number) => (mask & BIT_O) !== 0 && (mask & (BIT_P | BIT_D)) === 0;

const buildInitRanges = (opts: {
  existentes: DiaEspecial[];
  isBulk: boolean;
  defaultRange: [number, number] | null;
  assignedMask: number;
}): DialogRange[] => {
  const { existentes, isBulk, defaultRange, assignedMask } = opts;
  const fallbackMask = assignedMask > 0 ? assignedMask : MASK_ALL;

  if (!isBulk && existentes.length) {
    const grouped = new Map<string, DialogRange>();
    for (const e of existentes) {
      const safeMask = normalizeMask(Number(e.formatosMask ?? 0), assignedMask);
      const baseKey =
        e.tramoId ??
        `${hhmm(e.horaInicio)}|${hhmm(e.horaFin)}|${safeMask}|${Number(e.bloqueo ? 1 : 0)}|${e.comentario ?? ''}`;

      if (!grouped.has(baseKey)) {
        grouped.set(baseKey, {
          key: makeDialogRangeKey(),
          range: [toMinutes(e.horaInicio), toMinutes(e.horaFin)],
          mask: safeMask,
          centros: [],
        });
      }

      const slot = grouped.get(baseKey)!;
      if (e.idCentro != null) slot.centros.push(Number(e.idCentro));
    }

    const out = Array.from(grouped.values())
      .map((r) => ({ ...r, centros: dedupeCentros(r.centros) }))
      .sort((a, b) => a.range[0] - b.range[0] || a.range[1] - b.range[1]);

    if (out.length) return out;
  }

  if (defaultRange) {
    return [
      {
        key: makeDialogRangeKey(),
        range: defaultRange,
        mask: fallbackMask,
        centros: [],
      },
    ];
  }

  return [
    {
      key: makeDialogRangeKey(),
      range: [540, 1260],
      mask: fallbackMask,
      centros: [],
    },
  ];
};

function DiaEspecialDialog({
  open,
  date,
  profesorId,
  existentes,
  defaultRange,
  onClose,
  onSave,
}: DlgProps) {
  const { t } = useTranslation<'translation'>();
  const { rows: centros } = useCentros();
  const { assignedMask: formatsAssignedMask } = useProfesorFormatos(profesorId);
  const assigned = formatsAssignedMask || 0;
  const fallbackMask = assigned > 0 ? assigned : MASK_ALL;

  const dates = useMemo(() => (Array.isArray(date) ? date : [date]), [date]);
  const isBulk = dates.length > 1;
  const dateLabel = isBulk
    ? `${dates.length} ${t('specialDays.dialog.daysSelected', { defaultValue: 'días' })}`
    : dates[0];

  const [blockDay, setBlockDay] = useState(false);

  const init = useMemo<DialogRange[]>(
    () =>
      buildInitRanges({
        existentes,
        isBulk,
        defaultRange,
        assignedMask: assigned,
      }),
    [assigned, defaultRange, existentes, isBulk],
  );

  const [ranges, setRanges] = useState<DialogRange[]>(init);
  const [comentario, setComentario] = useState<string>(existentes[0]?.comentario ?? '');

  useEffect(() => {
    if (open) {
      setRanges(init);
      setBlockDay(false);
      setComentario(existentes[0]?.comentario ?? '');
    }
  }, [open, init, existentes]);

  const addRange = () =>
    setRanges((rs) => {
      const ordered = [...rs].map((r) => r.range).sort((a, b) => a[0] - b[0]);
      const dur = 120;
      for (let ini = 0; ini + dur <= 24 * 60; ini += 30) {
        const fin = ini + dur;
        const libre = ordered.every(([a, b]) => fin <= a || ini >= b);
        if (libre) {
          return [
            ...rs,
            {
              key: makeDialogRangeKey(),
              range: [ini, fin],
              mask: fallbackMask,
              centros: [],
            },
          ];
        }
      }
      return [
        ...rs,
        {
          key: makeDialogRangeKey(),
          range: [540, 1260],
          mask: fallbackMask,
          centros: [],
        },
      ];
    });

  const delRange = (idx: number) => setRanges((rs) => rs.filter((_, i) => i !== idx));
  const updRange = (idx: number, r: Range) => {
    setRanges((rs) =>
      rs.map((x, i) =>
        i === idx
          ? {
              ...x,
              range: [...r] as Range,
            }
          : x,
      ),
    );
  };

  const setRangeMask = (idx: number, nextMask: number) => {
    setRanges((rs) => rs.map((x, i) => (i === idx ? { ...x, mask: nextMask } : x)));
  };

  const toggleRangeBit = (idx: number, bit: number) => {
    setRanges((rs) =>
      rs.map((x, i) =>
        i === idx ? { ...x, mask: x.mask & bit ? x.mask & ~bit : x.mask | bit } : x,
      ),
    );
  };

  const setRangeCentros = (idx: number, nextCentros: number[]) => {
    setRanges((rs) =>
      rs.map((x, i) => (i === idx ? { ...x, centros: dedupeCentros(nextCentros) } : x)),
    );
  };

  const handleSave = async () => {
    // BLOQUEO DE DÍA COMPLETO
    if (blockDay) {
      const crear: CreateEspecialPayload[] = dates.map((d) => ({
        idProfesor: profesorId,
        fecha: d,
        horaInicio: '00:00',
        horaFin: '00:00',
        bloqueo: true,
        comentario: comentario?.trim() ? comentario.trim() : undefined,
      }));
      await onSave({ crear, eliminar: existentes.map((e) => e.id) });
      onClose();
      return;
    }

    // Normalizar rangos (min 15')
    const clean = ranges
      .map((r) => ({
        ...r,
        range: [floorToStep(r.range[0]), floorToStep(r.range[1])] as Range,
        mask: normalizeMask(r.mask, assigned),
        centros: dedupeCentros(r.centros),
      }))
      .filter((r) => r.range[1] - r.range[0] >= 15)
      .sort((a, b) => a.range[0] - b.range[0] || a.range[1] - b.range[1]);

    if (!clean.length) {
      onClose();
      return;
    }

    // Deduplicar posibles filas idénticas, consolidando centros
    const mergedBySlot = new Map<string, DialogRange>();
    for (const row of clean) {
      const k = `${row.range[0]}|${row.range[1]}|${row.mask}`;
      if (!mergedBySlot.has(k)) {
        mergedBySlot.set(k, { ...row, centros: [...row.centros] });
      } else {
        const current = mergedBySlot.get(k)!;
        current.centros = dedupeCentros([...current.centros, ...row.centros]);
      }
    }

    const normalized = Array.from(mergedBySlot.values());

    // AMPLIACIÓN / SUSTITUCIÓN POSITIVA
    const crear: CreateEspecialPayload[] = dates.flatMap((d) =>
      normalized.map((slot) => {
        const [ini, fin] = slot.range;
        const payload: CreateEspecialPayload = {
          idProfesor: profesorId,
          fecha: d,
          horaInicio: toStr(ini),
          horaFin: toStr(fin),
          formatos: maskToArray(slot.mask),
          comentario: comentario?.trim() ? comentario.trim() : undefined,
        };

        // Si es solo online, ignoramos centros explícitos para mantener semántica actual.
        if (!isOnlyOnlineMask(slot.mask)) {
          if (slot.centros.length > 1) payload.centros = slot.centros.slice();
          else if (slot.centros.length === 1) payload.idCentro = slot.centros[0];
        }

        return payload;
      }),
    );

    await onSave({ crear, eliminar: existentes.map((e) => e.id) });
    onClose();
  };

  const handleDeleteAll = async () => {
    if (existentes.length) await onSave({ crear: [], eliminar: existentes.map((e) => e.id) });
    onClose();
  };

  const centrosActivos = centros.filter((c) => c.activo);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('specialDays.dialog.title')} — {dateLabel}
      </DialogTitle>

      <DialogContent sx={{ pt: 3, overflow: 'visible' }}>
        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={blockDay}
                onChange={(_, checked) => {
                  setBlockDay(checked);
                }}
              />
            }
            label={t('specialDays.dialog.blockAll', {
              defaultValue: 'Bloquear día(s) (sin clases)',
            })}
          />

          {/* Comentario */}
          <TextField
            label="Comentario (opcional)"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            multiline
            minRows={2}
          />

          {!blockDay &&
            ranges.map((slot, idx) => {
              const [ini, fin] = slot.range;
              const allAssignedSelected =
                assigned > 0
                  ? (slot.mask & assigned) === assigned && (slot.mask & ~assigned) === 0
                  : slot.mask === MASK_ALL;
              const anyAssignedSelected =
                assigned > 0 ? (slot.mask & assigned) !== 0 : slot.mask !== 0;

              return (
                <Box key={slot.key} sx={{ p: 1.5, borderRadius: 1, border: '1px solid #E0E0E0' }}>
                  <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                    <TextField
                      type="time"
                      label={t('specialDays.dialog.start', { defaultValue: 'Inicio' })}
                      value={toStrForInput(ini)}
                      onChange={(e) => {
                        const mins = parseTimeToMin(e.target.value);
                        if (mins == null) return;
                        updRange(idx, [mins, fin]);
                      }}
                      inputProps={{ step: 900, min: '00:00', max: '23:59' }}
                      sx={{ minWidth: 140 }}
                    />

                    <TextField
                      type="time"
                      label={t('specialDays.dialog.end', { defaultValue: 'Fin' })}
                      value={toStrForInput(fin)}
                      onChange={(e) => {
                        const mins = parseTimeToMin(e.target.value);
                        if (mins == null) return;
                        updRange(idx, [ini, mins]);
                      }}
                      inputProps={{ step: 900, min: '00:00', max: '23:59' }}
                      sx={{ minWidth: 140 }}
                    />

                    <IconButton
                      size="small"
                      onClick={() => delRange(idx)}
                      sx={{
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'error.main', color: 'common.white' },
                      }}
                      aria-label="Eliminar"
                    >
                      <Delete fontSize="inherit" />
                    </IconButton>
                  </Stack>

                  <FormGroup row sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allAssignedSelected}
                          indeterminate={anyAssignedSelected && !allAssignedSelected}
                          onChange={(_, v) =>
                            setRangeMask(idx, v ? (assigned > 0 ? assigned : MASK_ALL) : 0)
                          }
                        />
                      }
                      label="Todos (asignados)"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(slot.mask & BIT_P) !== 0}
                          onChange={() => toggleRangeBit(idx, BIT_P)}
                        />
                      }
                      label="Presencial"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(slot.mask & BIT_O) !== 0}
                          onChange={() => toggleRangeBit(idx, BIT_O)}
                        />
                      }
                      label="Online"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(slot.mask & BIT_D) !== 0}
                          onChange={() => toggleRangeBit(idx, BIT_D)}
                        />
                      }
                      label="A domicilio"
                    />
                  </FormGroup>

                  <Autocomplete
                    multiple
                    options={centrosActivos}
                    getOptionLabel={(c) => c.nombre}
                    value={centrosActivos.filter((c) => slot.centros.includes(c.id))}
                    onChange={(_, vals) =>
                      setRangeCentros(
                        idx,
                        vals.map((v) => v.id),
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('specialDays.dialog.centerLabel', {
                          defaultValue: 'Centros del tramo',
                        })}
                      />
                    )}
                    sx={{ mt: 1, maxWidth: 500 }}
                    disabled={isOnlyOnlineMask(slot.mask)}
                  />

                  {idx < ranges.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              );
            })}

          {!blockDay && (
            <Button
              startIcon={<Add />}
              onClick={addRange}
              size="small"
              variant="outlined"
              sx={{ alignSelf: 'flex-start' }}
            >
              Añadir tramo
            </Button>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        {!!existentes.length && (
          <Button color="error" onClick={handleDeleteAll}>
            Eliminar todo
          </Button>
        )}
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave}>
          Listo
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ═══════════════════ DÍAS ESPECIALES (página) ═══════════════════ */
interface Props {
  profesorId: number;
  bloquesSemana: any[];
  weeklyContext: WeeklyContextSnapshot;
}

export default function DiasEspeciales({ profesorId, bloquesSemana, weeklyContext }: Props) {
  const { t, i18n } = useTranslation<'translation'>();

  /* ─── Rango del curso (sep→ago) ─── */
  const today = dayjs();
  const initY = today.month() >= 8 ? today.year() : today.year() - 1;
  const [startY, setStartY] = useState(initY);
  const desde = `${startY}-09-01`;
  const hasta = `${startY + 1}-08-31`;

  const {
    data: diasEsp = [],
    isLoading: loadingEsp,
    create,
    remove,
  } = useDiasEspeciales(profesorId ?? 0, desde, hasta);

  const [fechaSel, setFechaSel] = useState<string | null>(null);
  const [defaultRange, setDefaultRange] = useState<[number, number] | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [bulkDates, setBulkDates] = useState<string[] | null>(null);

  const sameCentro = (a?: number | null, b?: number | null) => (a ?? null) === (b ?? null);
  const samePeriod = useCallback(
    (r: any) => {
      const vhTarget = weeklyContext.finiteEnd ? weeklyContext.vigenciaHasta : null;
      return (
        (r.vigenciaDesde ?? '1970-01-01') === weeklyContext.vigenciaDesde &&
        (r.vigenciaHasta ?? null) === vhTarget
      );
    },
    [weeklyContext],
  );

  const defaultRangeFromWeekly = (wd: number): [number, number] | null => {
    const blocks = bloquesSemana.filter(
      (b) =>
        samePeriod(b) &&
        b.diaSemana === wd &&
        sameCentro(b.idCentro ?? null, weeklyContext.scopeCentro ?? null) &&
        (Number(b.formatosMask ?? BIT_P) & weeklyContext.scopeMask) !== 0,
    );
    if (!blocks.length) return null;
    const ini = Math.min(...blocks.map((b: any) => toMinutes(b.horaInicio)));
    const fin = Math.max(...blocks.map((b: any) => toMinutes(b.horaFin)));
    return [ini, fin];
  };

  const handleDayClick = (d: Dayjs) => {
    const str = d.format('YYYY-MM-DD');
    if (selectionMode) {
      setSelectedDates((prev) => {
        const n = new Set(prev);
        n.has(str) ? n.delete(str) : n.add(str);
        return n;
      });
      return;
    }
    setFechaSel(str);
    setDefaultRange(defaultRangeFromWeekly((d.day() + 6) % 7));
  };

  const handleSaveEspecial = async (p: { crear: CreateEspecialPayload[]; eliminar: number[] }) => {
    try {
      // evitar condiciones de carrera con el índice único (borrar → crear, secuencial)
      for (const id of p.eliminar) {
        await remove(id);
      }
      for (const dto of p.crear) {
        await create(dto);
      }
      setSnack({ open: true, msg: t('specialDays.snackbar.ok', { defaultValue: 'Guardado' }) });
    } catch {
      setSnack({
        open: true,
        msg: t('specialDays.snackbar.error', { defaultValue: 'Error al guardar' }),
        error: true,
      });
    }
  };

  const minDate = dayjs(`${startY}-09-01`);
  const maxDate = minDate.add(1, 'year').subtract(1, 'day');
  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => minDate.add(i, 'month')),
    [minDate],
  );

  // Fechas bloqueadas y especiales
  const blockedDates = useMemo(
    () =>
      diasEsp
        .filter((d) => d.bloqueo && hhmm(d.horaInicio) === '00:00' && hhmm(d.horaFin) === '23:59')
        .map((d) => d.fecha),
    [diasEsp],
  );
  const setBlocked = useMemo(() => new Set(blockedDates), [blockedDates]);
  const setEspecial = useMemo(
    () =>
      new Set(
        diasEsp
          .filter(
            (d) => !(d.bloqueo && hhmm(d.horaInicio) === '00:00' && hhmm(d.horaFin) === '23:59'),
          )
          .map((d) => d.fecha),
      ),
    [diasEsp],
  );

  const [snack, setSnack] = useState<{ open: boolean; msg: string; error?: boolean }>({
    open: false,
    msg: '',
  });

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={() => setStartY((y) => y - 1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6">
          {startY}-{String(startY + 1).slice(2)}
        </Typography>
        <IconButton onClick={() => setStartY((y) => y + 1)}>
          <ArrowForward />
        </IconButton>
      </Stack>

      {/* Selección múltiple */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={selectionMode}
              onChange={(_, v) => {
                setSelectionMode(v);
                if (!v) setSelectedDates(new Set());
              }}
            />
          }
          label={t('specialDays.selection.toggle', {
            defaultValue: 'Seleccionar varios días',
          })}
        />
        <Button
          size="small"
          onClick={() => setSelectedDates(new Set())}
          disabled={!selectedDates.size}
        >
          {t('specialDays.selection.clear', { defaultValue: 'Limpiar selección' })}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!selectedDates.size}
          onClick={() => {
            const arr = Array.from(selectedDates).sort();
            setBulkDates(arr);
            if (arr.length) {
              const first = dayjs(arr[0]);
              setDefaultRange(defaultRangeFromWeekly((first.day() + 6) % 7));
            }
          }}
        >
          {t('specialDays.selection.edit', { defaultValue: 'Editar selección' })}
        </Button>
      </Stack>

      {loadingEsp && <CircularProgress />}

      <Grid container columnSpacing={4} rowSpacing={6}>
        {months.map((m) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={m.format()}>
            <Typography align="center" sx={{ mb: 1, textTransform: 'capitalize' }}>
              {m.locale(i18n.language).format('MMMM')}
            </Typography>
            <DateCalendar
              referenceDate={m}
              views={['day']}
              value={null}
              minDate={minDate}
              maxDate={maxDate}
              onChange={() => {}}
              slots={{ day: ColoredDay as any, calendarHeader: () => null }}
              slotProps={{
                day: ({ day }) =>
                  ({
                    special: setEspecial.has(day.format('YYYY-MM-DD')),
                    blocked: setBlocked.has(day.format('YYYY-MM-DD')),
                    chosen: selectedDates.has(day.format('YYYY-MM-DD')),
                    onClick: () => handleDayClick(day),
                  }) as any,
              }}
              sx={{
                '.MuiPickersCalendarHeader-root .MuiPickersArrowSwitcher-root': {
                  display: 'none',
                },
              }}
            />
          </Grid>
        ))}
      </Grid>

      {fechaSel && (
        <DiaEspecialDialog
          open
          date={fechaSel}
          profesorId={profesorId}
          existentes={diasEsp.filter((d) => d.fecha === fechaSel)}
          defaultRange={defaultRange}
          onClose={() => setFechaSel(null)}
          onSave={handleSaveEspecial}
        />
      )}

      {bulkDates && (
        <DiaEspecialDialog
          open
          date={bulkDates}
          profesorId={profesorId}
          existentes={diasEsp.filter((d) => (bulkDates ?? []).includes(d.fecha))}
          defaultRange={defaultRange}
          onClose={() => {
            setBulkDates(null);
            setSelectedDates(new Set());
          }}
          onSave={async (p) => {
            await handleSaveEspecial(p);
            setSelectedDates(new Set());
          }}
        />
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.msg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={snack.error ? { sx: { bgcolor: 'error.main' } } : undefined}
      />
    </>
  );
}
